import { Button, ButtonVariant } from "@src/components/button/Button";
import { FilterField } from "@src/components/filtersPanel/FilterField";
import styles from "@src/components/filtersPanel/FilterLayout.module.scss";
import { FilterSection } from "@src/components/filtersPanel/FilterSection";
import { DateInput } from "@src/components/input/DateInput";
import { Input } from "@src/components/input/Input";
import { SelectYup } from "@src/components/select/SelectYup";
import { Switch } from "@src/components/switch/Switch";
import { pricingActions, purchasingActions, settingsActions } from "@src/store";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import React, { useCallback, useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

interface INewPoFilterForm {
	companyName: string;
	searchString: string;
	eventHeadlinerId: string;
	categoryId: string;
	venueId: string;
	startDate: string;
	endDate: string;
	includeParking: boolean;
}

const newPoQueryNames: Array<string> = [
	"searchString",
	"eventHeadlinerId",
	"categoryId",
	"venueId",
	"startDate",
	"endDate",
	"includeParking",
];

const newPoFiltersEmptyValues = {
	searchString: "",
	eventHeadlinerId: "",
	categoryId: "",
	venueId: "",
	startDate: "",
	endDate: "",
	includeParking: false,
};

const MAX_VISIBLE_OPTIONS = 100;

export const NewPoHeader: React.FC = () => {
	const [params, setParams] = useSearchParams();
	const dispatch = useAppDispatch();

	const companyName = useAppSelector((state) => state.settings.companyName);
	const isAdmin = useAppSelector((state) => state.settings.isAdmin);
	const { data: companies, loading: companiesLoading } = useAppSelector((state) => state.pricing.companies);

	const { data: poHeadliners, loading: headlinersLoading } = useAppSelector((state) => state.purchasing.poHeadliners);
	const { data: poCategories, loading: categoriesLoading } = useAppSelector((state) => state.purchasing.poCategories);
	const { data: poVenues, loading: venuesLoading } = useAppSelector((state) => state.purchasing.poVenues);

	const setFormValues = useCallback(
		(p: URLSearchParams): INewPoFilterForm => ({
			companyName: companyName ?? "",
			searchString: p.get("searchString") ?? "",
			eventHeadlinerId: p.get("eventHeadlinerId") ?? "",
			categoryId: p.get("categoryId") ?? "",
			venueId: p.get("venueId") ?? "",
			startDate: p.get("startDate") ?? "",
			endDate: p.get("endDate") ?? "",
			includeParking: p.get("includeParking") === "true",
		}),
		[companyName],
	);

	const { control, handleSubmit, reset, setValue, getValues, watch } = useForm<INewPoFilterForm>({
		mode: "onChange",
		defaultValues: setFormValues(params),
	});

	const selectedCompany = watch("companyName");
	const selectedCategoryId = watch("categoryId");
	const includeParking = watch("includeParking");

	const categoryOptions = useMemo(
		() =>
			poCategories
				.filter((x) => x.Cat_ID > 0)
				.map((x) => ({ label: x.Cat_Desc, value: x.Cat_ID }))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[poCategories],
	);
	const headlinerOptions = useMemo(
		() =>
			poHeadliners
				.filter((x) => x.Event_Headliner_ID > 0)
				.filter((x) => !selectedCategoryId || String(x.Cat_ID) === String(selectedCategoryId))
				.map((x) => ({ label: x.Event_Headliner, value: x.Event_Headliner_ID }))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[poHeadliners, selectedCategoryId],
	);
	const venueOptions = useMemo(
		() =>
			poVenues
				.filter((x) => x.Venue_ID > 0)
				.map((x) => ({ label: x.Venue_Name, value: x.Venue_ID }))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[poVenues],
	);
	const companyOptions = companies.map((x) => ({ label: x, value: x }));
	const showCompanyFilter = isAdmin || companies.length > 1;

	useEffect(() => {
		if ((selectedCompany ?? "") !== (companyName ?? "")) {
			dispatch(settingsActions.setCompanyName(selectedCompany ?? ""));
		}
	}, [selectedCompany, companyName, dispatch]);

	useEffect(() => {
		if (!companies.length && !companiesLoading) dispatch(pricingActions.listCompanies({}));
	}, [companies.length, companiesLoading, dispatch]);

	useEffect(() => {
		if (!poHeadliners.length && !headlinersLoading) dispatch(purchasingActions.listPoHeadliners({}));
		if (!poCategories.length && !categoriesLoading) dispatch(purchasingActions.listPoCategories({}));
		if (!poVenues.length && !venuesLoading) dispatch(purchasingActions.listPoVenues({}));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch]);

	useEffect(() => {
		reset(setFormValues(params));
	}, [params, reset, setFormValues]);

	const handleCategoryChange = () => {
		setValue("eventHeadlinerId", "", { shouldDirty: true });
		setValue("venueId", "", { shouldDirty: true });
	};

	const onSubmit: SubmitHandler<INewPoFilterForm> = (data) => {
		const queryParams: Record<string, string> = {
			searchString: data.searchString.trim(),
			eventHeadlinerId: data.eventHeadlinerId,
			categoryId: data.categoryId,
			venueId: data.venueId,
			startDate: data.startDate,
			endDate: data.endDate,
			includeParking: data.includeParking ? "true" : "",
		};
		Object.entries(queryParams).forEach(([key, value]) => {
			value ? params.set(key, value) : params.delete(key);
		});
		params.set("s", String((Number(params.get("s")) || 0) + 1));
		setParams(params);
	};

	const handleResetFilters = () => {
		newPoQueryNames.forEach((key) => params.delete(key));
		params.delete("s");
		setParams(params);
		reset({ ...newPoFiltersEmptyValues, companyName: companyName ?? "" });
	};

	return (
		<form className={styles.panelForm} onSubmit={handleSubmit(onSubmit)}>
			<div className={styles.panelBody}>
				{showCompanyFilter ? (
					<FilterSection title="Company">
						<FilterField label="Company name">
							<SelectYup
								isLoading={companiesLoading}
								name="companyName"
								placeholder="Search for company"
								control={control}
								options={companyOptions}
							/>
						</FilterField>
					</FilterSection>
				) : null}

				<FilterSection title="Search">
					<FilterField label="Search">
						<Input control={control} name="searchString" placeholder="Search events" />
					</FilterField>

					<div className={styles.row}>
						<FilterField label="Event start date">
							<DateInput
								popperStrategy="fixed"
								control={control}
								name="startDate"
								placeholder="Event start date"
								popperPlacement="bottom-start"
								onChange={(value) => {
									setValue("startDate", value, { shouldDirty: true });
									if (value && !getValues("endDate")) setValue("endDate", value, { shouldDirty: true });
								}}
							/>
						</FilterField>
						<FilterField label="Event end date">
							<DateInput
								popperStrategy="fixed"
								control={control}
								name="endDate"
								placeholder="Event end date"
								popperPlacement="bottom-end"
							/>
						</FilterField>
					</div>
				</FilterSection>

				<FilterSection title="Filters">
					<FilterField label="Event category">
						<SelectYup
							isLoading={categoriesLoading}
							name="categoryId"
							placeholder="Choose category"
							control={control}
							options={categoryOptions}
							onValueChange={handleCategoryChange}
						/>
					</FilterField>

					<FilterField label="Event headliner">
						<SelectYup
							isLoading={headlinersLoading}
							name="eventHeadlinerId"
							placeholder="Choose headliner"
							control={control}
							options={headlinerOptions}
							maxVisibleOptions={MAX_VISIBLE_OPTIONS}
						/>
					</FilterField>

					<FilterField label="Event venue">
						<SelectYup
							isLoading={venuesLoading}
							name="venueId"
							placeholder="Choose venue"
							control={control}
							options={venueOptions}
							maxVisibleOptions={MAX_VISIBLE_OPTIONS}
						/>
					</FilterField>

					<FilterField label="Include parking">
						<Switch
							checked={includeParking}
							onChange={(checked) => setValue("includeParking", checked, { shouldDirty: true })}
							label={includeParking ? "Included" : "Excluded"}
						/>
					</FilterField>
				</FilterSection>
			</div>

			<div className={styles.panelFooter}>
				<Button type="button" variant={ButtonVariant.Secondary} onClick={handleResetFilters}>
					Reset filters
				</Button>
				<Button type="submit">Search</Button>
			</div>
		</form>
	);
};
